'use client';

import { Card } from '@axo/ui-core/components/Card';
import { Stack } from '@axo/ui-core/components/layout/item';
import { Heading, Text } from '@axo/ui-core/components/typography';
import { useCSSVariable } from '@axo/ui-core/utils/useCSSVariable';
import { clsx } from 'clsx';
import { ReactElement, ReactNode } from 'react';
import { PoweredBy } from '../../components/PoweredBy';
import type { PoweredByProps } from '../../components/PoweredBy/PoweredBy';
import {
  ProgressTag,
  ProgressTagProps,
} from '../../components/ProgressTag/ProgressTag';
import { USPsList } from '../../components/USPsList';
import { TrustPilotProps, TrustPilotWidget } from '../TrustPilotWidget';

import styles from './loanProductAside.module.scss';

export type LoanProductAsideProps = {
  children?: ReactNode;
  labels: {
    title?: ReactNode;
    description?: ReactNode;
    usps?: ReactNode[];
    progress?: Pick<ProgressTagProps, 'progress' | 'labels'>;
    trustpilot?: Partial<Pick<TrustPilotProps, 'data' | 'market'>>;
    wlPoweredBy?: Pick<PoweredByProps, 'locale' | 'brand'>;
  };
  className?: string;
} & HTMLDataAttributes;

/**
 * LoanProductAside component
 *
 * responsive layout for loan product aside
 */
export const LoanProductAside = ({
  children,
  labels,
  className,
  ...props
}: LoanProductAsideProps): ReactElement => {
  const { value: isDesktopLayout = false, elRef } = useCSSVariable(
    '--is-desktop-layout'
  );

  return (
    <Stack
      className={clsx(styles.loanProductAside, className)}
      gap="m"
      align="start"
      _ref={elRef}
      {...props}
    >
      {labels.title && (
        <Heading size={'l'} className={styles.title}>
          {labels.title}
        </Heading>
      )}
      {labels.description && (
        <Text as={'p'} className={styles.description}>
          {labels.description}
        </Text>
      )}
      {children}
      {labels.usps && (
        <USPsList className={styles.usps}>
          {labels.usps.map((usp, index) => (
            <USPsList.Item key={index} iconName={'check'}>
              {usp}
            </USPsList.Item>
          ))}
        </USPsList>
      )}
      {labels.progress && (
        <ProgressTag className={styles.progress} {...labels.progress} />
      )}
      {labels.trustpilot?.data && (
        <Card spacing={'3xs'}>
          <TrustPilotWidget
            className={styles.trustpilot}
            {...(labels.trustpilot as TrustPilotProps)}
            // variant={isDesktopLayout === 'true' ? 'combo' : 'micro-form'}
            variant={'mini-form'}
          />
        </Card>
      )}
      {labels.wlPoweredBy && (
        <PoweredBy {...labels.wlPoweredBy} className={styles.poweredBy} />
      )}
    </Stack>
  );
};
